import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import IbButton from '../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../components/common/IbModal';
import {
  ApiKeyTenantScopeRole,
  ApiKeyProjectScopeRole,
  ApiKeyAgentScopeRole,
  ApiKeyCreationRequest,
  ApiKeyModel,
  ApiKeyScopeRef,
  ApiKeyScopeRefType,
  ApiKeyUpdatingRequest,
  ApiKeyScopeRole,
} from '../../../../gamma-api';
import IbTypography from '../../../components/common/IbTypography';
import IbInput from '../../../components/common/IbInput';
import IbSelect from '../../../components/common/IbSelect';
import { mask, MaskingMode } from '../../../utils/stringUtil';
import { X_TOKEN_LENGTH, X_TOKEN_VISIBLE_LENGTH } from '../const';
import IbIcon from '../../../components/common/IbIcon';
import { CheckFieldValueFnMap, useValidationForm } from '../../../utils/validationUtil';
import { ValidationErrors } from '../../../types';

import {
  FORM_ITEM_CLASS_NAME,
  MAIN_CLASS_NAME,
  EMPTY_API_KEY,
  X_TOKEN_CLASS_NAME,
  COPY_ID_BUTTON_CLASS_NAME,
  MODAL_CONTENT_CLASS_NAME,
} from './const';

type ApiKeyForm = {
  name: string;
  access: ApiKeyScopeRole[];
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ApiKeyForm> = {
  name: (value) => !!value.length,
  access: (value) => !!value.length,
};

interface IApiKeyModalProps {
  visible: boolean;
  editApiKey?: ApiKeyModel;
  scopeRef: ApiKeyScopeRef;
  onAdd: (apiKey: ApiKeyCreationRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onEdit: (apiKey: ApiKeyUpdatingRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel: () => void;
}

const ApiKeyModal: React.FC<IApiKeyModalProps> = ({ visible, editApiKey, scopeRef, onAdd, onEdit, onCancel }) => {
  const { t } = useTranslation();

  const [saving, setSaving] = useState(false);

  const [apiKey, setApiKey] = useState<ApiKeyModel>(EMPTY_API_KEY);

  const initialFormValues: ApiKeyForm = {
    name: editApiKey?.name ?? '',
    access: editApiKey?.roles ?? [],
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ApiKeyForm>(initialFormValues, checkFieldValueFnMap);

  const onVisiblePropChange = () => {
    if (!visible) return;

    setSaving(false);
    setApiKey(editApiKey ?? { ...EMPTY_API_KEY, scope_ref: scopeRef });
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setApiKey({ ...apiKey, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onApiKeyRoleChange = (value: SelectValue) => {
    setApiKey({ ...apiKey, roles: value as ApiKeyScopeRole[] });
    onFieldChange('access', value as ApiKeyScopeRole[]);
  };

  const onApiKeyRoleBlur = () => onFieldBlur('access');

  const onCreateButtonClick = async () => {
    if (!validate()) return;

    setSaving(true);
    await onAdd({ name: apiKey.name, scope_ref: scopeRef, roles: apiKey.roles }, onSubmitErrors);
    setSaving(false);
  };

  const onSaveButtonClick = async () => {
    if (!editApiKey || !validate()) return;

    setSaving(true);
    await onEdit({ name: apiKey.name, roles: apiKey.roles }, onSubmitErrors);
    setSaving(false);
  };

  const getRoleOptions = (): { value: string; label: string }[] => {
    if (scopeRef.type === ApiKeyScopeRefType.Tenant) {
      return [{ value: ApiKeyTenantScopeRole.Integration, label: t('API Keys: Tenant: Integration') }];
    }

    if (scopeRef.type === ApiKeyScopeRefType.Project) {
      return [
        { value: ApiKeyProjectScopeRole.Chat, label: t('API Keys: Project: Chat') },
        { value: ApiKeyProjectScopeRole.DataManagement, label: t('API Keys: Project: Data management') },
      ];
    }

    if (scopeRef.type === ApiKeyScopeRefType.Agent) {
      return [{ value: ApiKeyAgentScopeRole.Chat, label: t('API Keys: Agent: Chat') }];
    }

    return [];
  };

  const renderModalContent = () => {
    const roleOptions = getRoleOptions();

    const onCopyIdButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      navigator.clipboard.writeText(apiKey.x_token);
    };

    return (
      <div className={MODAL_CONTENT_CLASS_NAME}>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('API Keys: Name: Title')}</IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={apiKey.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, t('API Keys: Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('API Keys: Access: Title')}</IbTypography.Paragraph>
          <IbSelect
            mode="multiple"
            options={roleOptions}
            status={getShowError(validationState.access) ? 'error' : 'default'}
            value={apiKey.roles.map((role) => role as string)}
            onBlur={onApiKeyRoleBlur}
            onChange={onApiKeyRoleChange}
          />
          {getShowError(validationState.access) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.access, t('API Keys: Access: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        {editApiKey && (
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('API Keys: Key')}</IbTypography.Paragraph>
            <div className={X_TOKEN_CLASS_NAME}>
              <IbTypography.Paragraph>
                {mask(apiKey.x_token, MaskingMode.End, X_TOKEN_LENGTH, X_TOKEN_VISIBLE_LENGTH)}
              </IbTypography.Paragraph>
              <IbButton
                className={COPY_ID_BUTTON_CLASS_NAME}
                icon={<IbIcon iconName="copy-one" />}
                type="link"
                onClick={onCopyIdButtonClick}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editApiKey ? (
          <IbButton type="primary" onClick={onSaveButtonClick}>
            {t('API Keys: Save')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {t('API Keys: Add')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {t('API Keys: Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editApiKey ? t('API Keys: Edit key') : t('API Keys: Add key');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderModalContent()}
    </IbModal>
  );
};

export default ApiKeyModal;
